import config from '../config';
import authService from '../services/auth';
import HTTPError from './HTTPError';
import { getLanguageCode } from './i18n/IntlContext';

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type RequestInfo = Pick<RequestInit, Exclude<keyof RequestInit, 'headers'>> & {
    headers?: Record<string, string>,
    auth?: false | string | [string, string] | null,
    as?: 'none' | 'json' | 'blob' | 'text'
};

export type ResponseInfo<T> = Response & { result: T };

const convertResult = <T>(data: any): T => {
    if (data)
        if (typeof data === 'string') {
            let newValue = new Date(data);

            if (newValue && !Number.isNaN(newValue.valueOf()) && `${newValue.toISOString().slice(0, -5)}Z` === data)
                data = newValue;
        } else if (typeof data === 'object') {
            for (const key in data)
                data[key] = convertResult(data[key]);
        }

    return data;
};

const handleRequest = (url: string, method: Method, data?: any, request?: RequestInfo): [string, RequestInfo] => {
    request = request || {} as RequestInfo;

    request.method = method;

    if (data !== undefined)
        request.body = data;

    request.headers = request.headers || {};

    if (!request.headers['Content-Type'] && request.body) {
        request.headers['Content-Type'] = 'application/json';

        request.body = JSON.stringify(request.body);
    }

    if (!request.headers['Accept-Language'])
        request.headers['Accept-Language'] = getLanguageCode();

    if (!request.auth && request.auth !== false)
        request.auth = authService.getActiveUserToken();

    if (request.auth) {
        request = {
            ...request,
            headers: {
                ...request.headers,
                Authorization: typeof request.auth === 'string' ? `Bearer ${request.auth}` : `Basic ${btoa(request.auth.join(':'))}`
            }
        };

        delete request.auth;
    }

    return [url.startsWith('https://') ? url : `${config.api}${url}`, request];
};

const handleResponse = async <T>(response: Response, request?: RequestInfo) => {
    let result: any = null;

    const contentType = response.headers.get('Content-Type')?.toLowerCase();

    try {
        if (request?.as === 'text')
            result = await response.text();
        else if (request?.as === 'blob')
            result = await response.blob();
        else if (request?.as === 'json' || (request?.as !== 'none' && contentType?.startsWith('application/json')))
            result = convertResult<T>(await response.json());
    } catch (error) {}

    if (response.ok) {
        return Object.assign(response, { result }) as ResponseInfo<T>;
    } else {
        throw new HTTPError(Object.assign(response, { result }));
    }
};

const request = async <T>(url: string, method: Method, data?: any, request?: RequestInfo) =>
    handleResponse<T>(await fetch(...handleRequest(url, method, data, request)), request);

const get = <T = any>(url: string, config?: RequestInfo) => http.request<T>(url, 'GET', undefined, config);

const post = <T = any>(url: string, data?: any, config?: RequestInfo) => http.request<T>(url, 'POST', data, config);

const put = <T = any>(url: string, data?: any, config?: RequestInfo) => http.request<T>(url, 'PUT', data, config);

const patch = <T = any>(url: string, data?: any, config?: RequestInfo) => http.request<T>(url, 'PATCH', data, config);

const del = <T = any>(url: string, config?: RequestInfo) => http.request<T>(url, 'DELETE', undefined, config);

const http = { request, get, post, put, patch, delete: del };

export default http;

/**
 * Não use diretamente, apenas para testes unitários
 */
export const __testsOnly__ = { handleRequest, handleResponse };